import { useTranslation } from 'react-i18next';
import type { TFunction } from 'i18next';
import { CheckCircle2, CircleSlash, Loader2, XCircle } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { probeVerdict, type ProbeVerdict } from '@/lib/providers/integration-state';
import type { IntegrationProbeOutcome, IntegrationProbeResult } from '@/lib/providers/types';

/**
 * "Anda?" as a button (slice E2).
 *
 * Renders the operator-triggered probe for ONE integration: the run control,
 * the per-step outcomes with their latencies, and the verdict. It consumes the
 * same seam as the shipped diagnostics card (`POST /v1/providers/{vendor}/test`)
 * through the one shared client in `lib/providers/api.ts`, and reuses that
 * card's `providers.vendorTest.*` copy so the page speaks one vocabulary.
 *
 * PRESENTATIONAL ON PURPOSE: the mutation lives in the row, which needs the
 * same outcome for its status cell. Two independent copies of the probe
 * state would let the chip say "not tested" under a rendered result.
 *
 * ── WHAT THIS ADDS OVER THE SHIPPED CARD ─────────────────────────────────────
 * The FOUR-WAY verdict. A descriptor world has a state a single-vendor
 * diagnostics button never had to model:
 *
 *   pass · fail · NOT PROBEABLE · NOT TESTED
 *
 * `notProbeable` means the vendor's profile declares no read-only endpoint we
 * may call (a webhook-driven vendor, or a mock). Reading `ok` on such a result
 * renders "Connection OK" over zero steps — claiming we verified a connection
 * we never opened. It is rendered as PRESENT BUT NOT PROBEABLE instead, and
 * never as a failure.
 *
 * `not-tested` is the absence of a result, and stays visibly distinct from a
 * failed one: absence of a probe is not a failed probe.
 *
 * REALTIME: none. This is a user-triggered mutation that spends a real vendor
 * call — it runs when the operator presses the button and at no other time.
 */

interface IntegrationProbeProps {
  vendor: string;
  /** Test/DOM id for this instance. `vendor` alone is NOT unique — the live
   *  inventory carries `amadeus`, `sabre` and `mock` in two categories each, so
   *  the row passes its category-qualified key. Defaults to `vendor`. */
  testId?: string;
  outcome: IntegrationProbeOutcome | undefined;
  isPending: boolean;
  isError: boolean;
  onRun: () => void;
  /** Id of the card's heading, so the control names its own integration. */
  labelledBy?: string;
}

export function IntegrationProbe({
  vendor,
  testId,
  outcome,
  isPending,
  isError,
  onRun,
  labelledBy,
}: IntegrationProbeProps) {
  const { t } = useTranslation('network');
  const id = testId ?? vendor;
  const result = outcome?.kind === 'result' ? outcome.result : undefined;
  const verdict = probeVerdict(result);

  return (
    <div className="space-y-2" data-testid={`integration-probe-${id}`} data-verdict={verdict}>
      <Button
        variant="outline"
        className="min-h-11 w-full"
        onClick={onRun}
        disabled={isPending}
        aria-describedby={labelledBy ?? undefined}
        data-testid={`integration-probe-run-${id}`}
      >
        {isPending ? (
          <>
            <Loader2 className="h-4 w-4 animate-spin" aria-hidden />
            {t('providers.vendorTest.running')}
          </>
        ) : (
          t('providers.vendorTest.run')
        )}
      </Button>

      {isError ? (
        <p className="text-xs text-rose-600" data-testid={`integration-probe-error-${id}`}>
          {t('providers.vendorTest.error')}
        </p>
      ) : null}

      {outcome?.kind === 'rate_limited' ? (
        <p
          className="rounded-md border border-amber-500/40 bg-amber-500/10 p-2 text-xs text-amber-700 dark:text-amber-400"
          data-testid={`integration-probe-rate-limited-${id}`}
        >
          {outcome.retryAfterMs
            ? t('providers.vendorTest.rateLimitedWithWait', {
                seconds: Math.ceil(outcome.retryAfterMs / 1000),
              })
            : t('providers.vendorTest.rateLimited')}
        </p>
      ) : null}

      {result ? (
        <div className="space-y-1.5" data-testid={`integration-probe-result-${id}`}>
          {result.steps.length > 0 ? (
            <ul className="space-y-1">
              {result.steps.map((s) => (
                <li
                  key={s.step}
                  className="flex flex-wrap items-start gap-x-2 gap-y-0.5 text-xs"
                  data-testid={`integration-probe-step-${id}-${s.step}`}
                  data-ok={s.ok}
                >
                  {s.ok ? (
                    <CheckCircle2
                      className="mt-0.5 h-3.5 w-3.5 shrink-0 text-emerald-600"
                      aria-label={t('providers.vendorTest.stepOk')}
                    />
                  ) : (
                    <XCircle
                      className="mt-0.5 h-3.5 w-3.5 shrink-0 text-rose-600"
                      aria-label={t('providers.vendorTest.stepFailed')}
                    />
                  )}
                  <span className="min-w-0 break-words font-medium">{s.step}</span>
                  <span className="tabular-nums text-muted-foreground">{s.latencyMs} ms</span>
                  {s.detail ? (
                    <span className={s.ok ? 'text-muted-foreground' : 'text-rose-600'}>
                      · {s.detail}
                    </span>
                  ) : null}
                </li>
              ))}
            </ul>
          ) : null}
          <ProbeVerdictLine verdict={verdict} result={result} t={t} id={id} />
        </div>
      ) : null}
    </div>
  );
}

/** The one line that says what the run MEANT. Reading `ok` here instead of the
 *  verdict is the bug this component exists to prevent. */
function ProbeVerdictLine({
  verdict,
  result,
  t,
  id,
}: {
  verdict: ProbeVerdict;
  result: IntegrationProbeResult;
  t: TFunction<'network'>;
  /** The row's category-qualified key, not the bare vendor token. */
  id: string;
}) {
  const when = t('providers.vendorTest.testedAt', { when: relativeTime(result.testedAt, t) });

  if (verdict === 'not-probeable') {
    return (
      <div
        className="rounded-md border border-dashed border-border bg-muted/40 p-2 text-[11px] text-muted-foreground"
        data-testid={`integration-probe-not-probeable-${id}`}
      >
        <p className="flex items-center gap-1.5 font-medium text-foreground">
          <CircleSlash className="h-3.5 w-3.5 shrink-0" aria-hidden />
          {t('providers.integrations.probe.notProbeable')}
        </p>
        <p className="mt-0.5">{t('providers.integrations.probe.notProbeableHint')}</p>
        <p className="mt-0.5">{when}</p>
      </div>
    );
  }

  return (
    <p className="text-[11px] text-muted-foreground">
      <span
        className={
          verdict === 'pass' ? 'font-medium text-emerald-600' : 'font-medium text-rose-600'
        }
      >
        {verdict === 'pass' ? t('providers.vendorTest.passed') : t('providers.vendorTest.failed')}
      </span>{' '}
      · {when}
    </p>
  );
}

/** Localised "12s ago" / "3m ago" / "1h ago"; '' when unparseable. */
function relativeTime(iso: string, t: TFunction<'network'>): string {
  const at = new Date(iso).getTime();
  if (Number.isNaN(at)) return '';
  const secs = Math.max(0, Math.floor((Date.now() - at) / 1000));
  if (secs < 60) return t('providers.vendorTest.ago.seconds', { count: secs });
  if (secs < 3600) return t('providers.vendorTest.ago.minutes', { count: Math.floor(secs / 60) });
  return t('providers.vendorTest.ago.hours', { count: Math.floor(secs / 3600) });
}
